import React, {useMemo, useState} from "react";
import { CalendarCheck, Clock, Brain, ListTodo } from "lucide-react";

function formatMinutesLabel(minutes = 0) {
    return `${minutes}m`;
}

const EMPTY_STATS = {
    completedTasks: 0,
    completedPomodoros: 0,
    focusSeconds: 0,
};

const TIME_RANGE_OPTIONS = [
    {value: 'DAILY', label: 'Today'},
    {value: 'WEEKLY', label: 'This Week'},
    {value: 'MONTHLY', label: 'This Month'},
    {value: 'ALL_TIME', label: 'All Time'},
];

export default function TodayOverview({
    recallDue = 0,
    statsByRange = {},
    isLoadingByRange = {},
    hasErrorByRange = {},
    defaultTimeRange = 'DAILY',
}) {
    const [activeTimeRange, setActiveTimeRange] = useState(defaultTimeRange);

    const activeOption = useMemo(
        () => TIME_RANGE_OPTIONS.find((option) => option.value === activeTimeRange) ?? TIME_RANGE_OPTIONS[0],
        [activeTimeRange]
    );

    const activeStats = statsByRange[activeTimeRange] ?? EMPTY_STATS;
    const focusMinutes = Math.floor((activeStats.focusSeconds ?? 0) / 60);
    const isLoading = Boolean(isLoadingByRange[activeTimeRange]);
    const hasError = Boolean(hasErrorByRange[activeTimeRange]);

    const tasksValue = isLoading ? 'Loading...' : hasError ? '—' : activeStats.completedTasks;
    const pomodorosValue = isLoading ? 'Loading...' : hasError ? '—' : activeStats.completedPomodoros;
    const focusValue = isLoading ? 'Loading...' : hasError ? '—' : formatMinutesLabel(focusMinutes);

    return (
        <div className="bg-[#1E1F25] p-6 rounded-xl shadow-lg border border-[#2A2B32]">
            <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <h2 className="text-xl font-semibold text-gray-200">{activeOption.label} Overview</h2>

                <div className="flex flex-wrap gap-2">
                    {TIME_RANGE_OPTIONS.map((option) => {
                        const isActive = option.value === activeTimeRange;

                        return (
                            <button
                                key={option.value}
                                type="button"
                                onClick={() => setActiveTimeRange(option.value)}
                                className={`rounded-full border px-3 py-1.5 text-xs font-medium transition ${isActive ? 'border-cyan-400/50 bg-cyan-400/15 text-cyan-200' : 'border-white/10 bg-white/5 text-gray-400 hover:bg-white/10 hover:text-gray-200'}`}
                            >
                                {option.label}
                            </button>
                        );
                    })}
                </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">

                {/* Tasks */}
                <div className="bg-[#2A2B32] rounded-xl p-4 flex flex-col gap-2 hover:bg-[#32333C] transition">
                    <ListTodo size={22} className="text-blue-400" />
                    <div className="text-lg font-bold text-gray-100">{tasksValue}</div>
                    <div className="text-sm text-gray-400">Completed Tasks</div>
                </div>

                {/* Recall */}
                <div className="bg-[#2A2B32] rounded-xl p-4 flex flex-col gap-2 hover:bg-[#32333C] transition">
                    <Brain size={22} className="text-purple-400" />
                    <div className="text-lg font-bold text-gray-100">{recallDue}</div>
                    <div className="text-sm text-gray-400">Recall Due</div>
                </div>

                {/* Completed Pomodoros */}
                <div className="bg-[#2A2B32] rounded-xl p-4 flex flex-col gap-2 hover:bg-[#32333C] transition">
                    <CalendarCheck size={22} className="text-green-400" />
                    <div className="text-lg font-bold text-gray-100">{pomodorosValue}</div>
                    <div className="text-sm text-gray-400">Pomodoros</div>
                </div>

                {/* Focus Completed */}
                <div className="bg-[#2A2B32] rounded-xl p-4 flex flex-col gap-2 hover:bg-[#32333C] transition">
                    <Clock size={22} className="text-yellow-400" />
                    <div className="text-lg font-bold text-gray-100">{focusValue}</div>
                    <div className="text-sm text-gray-400">Focus Time</div>
                </div>
            </div>
        </div>
    );
}
